import React from 'react';
import PropTypes from 'prop-types';
import { StyledEngineProvider } from '@mui/material/styles';

import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';

import VpButton from '@vp-ui/components/VpButton';
import styles from '@vp-ui/components/VpPlanCards/VpPlanCard.module.less';

const ANNUAL_BILLING_LABEL = 'billed annually';
const ENTERPRISE_BILLING_LABEL = 'Custom Pricing';

export const getActionButtonBehavior = {
  SAME_PLAN: {
    isButtonDisabled: true,
    buttonLabel: 'Current Plan',
    buttonProps: {
      color: 'neutral'
    }
  },
  FREE_TRIAL: {
    isButtonDisabled: true,
    buttonLabel: 'Current Plan - Free Trial',
    buttonProps: {
      color: 'neutral'
    }
  },
  ELIGIBLE_PLAN: {
    isButtonDisabled: false,
    buttonLabel: 'Upgrade'
  },
  ENTERPRISE_PLAN: {
    isButtonDisabled: false,
    buttonLabel: 'Contact Sales',
    buttonProps: {
      variant: 'outlined',
      color: 'neutral'
    }
  },
  APPSUMO_PLAN: {
    isButtonDisabled: false,
    buttonLabel: 'Add Codes'
  }
};

export const getPricingLabel = (plan, pricePerMonth, isMonthlyPricing) => {
  const { monthlyBilling, annualBilling } = pricePerMonth;

  if (plan === 'Enterprise') {
    return ENTERPRISE_BILLING_LABEL;
  }

  const price = isMonthlyPricing ? monthlyBilling : annualBilling;
  const billingPeriod = ' / month';
  const billingPeriodDetail = isMonthlyPricing ? '' : `, ${ANNUAL_BILLING_LABEL}`;

  return `$${price}${billingPeriod}${billingPeriodDetail}`;
};

export const VpPlanCard = ({
  plan,
  planDescription,
  pricePerMonth,
  isMonthlyPricing,
  features = [],
  callToActionBehavior,
  callToActionHandler,
  handleCancelSubscription = () => {},
  codeCount,
  planKey,
  disableUpgrade = false
}) => {
  const actionButtonBehavior = getActionButtonBehavior[callToActionBehavior];
  if (!actionButtonBehavior) {
    return null;
  }
  const { isButtonDisabled, buttonLabel, buttonProps = {} } = actionButtonBehavior;
  const pricingLabel = getPricingLabel(plan, pricePerMonth, isMonthlyPricing);
  const submissionLimit = codeCount * 100;

  return (
    <StyledEngineProvider injectFirst>
      <Card data-testid="vp-plan-card" className={styles.vpPlanCardLayout}>
        <CardContent sx={{ padding: 0 }}>
          <Typography variant="h3" sx={{ fontSize: 18, marginBottom: '5px' }}>
            {plan}
          </Typography>
          <Typography component="div" sx={{ marginBottom: '12px' }}>
            {planDescription}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 500, fontSize: 18, marginBottom: '16px' }}>
            {plan === 'AppSumo' ? (
              <Chip label={`${submissionLimit} Submissions Per Month`} variant="colorTag" />
            ) : (
              pricingLabel
            )}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 700, marginBottom: '5px' }}>
            {plan === 'Pro'
              ? 'Everything in Basic, plus:'
              : plan === 'Premium'
              ? 'Everything in Pro, plus:'
              : plan === 'Enterprise'
              ? 'Everything in Premium, plus:'
              : 'Get started with these features:'}
          </Typography>
          <Typography component="ul" sx={{ marginBottom: '15px' }}>
            {features.map((feature, idx) => (
              <li key={`${feature}${idx}`}>{feature}</li>
            ))}
          </Typography>
        </CardContent>
        <CardActions
          sx={{ padding: 0, flexDirection: 'column', alignItems: 'center', marginTop: 'auto' }}
        >
          <VpButton
            data-testid="vp-plan-card-button"
            onClick={() => callToActionHandler(planKey)}
            disabled={isButtonDisabled || disableUpgrade}
            variant="contained"
            sx={{ width: '100%' }}
            {...buttonProps}
          >
            {buttonLabel}
          </VpButton>
          {callToActionBehavior === 'FREE_TRIAL' && (
            <VpButton
              data-testid="vp-plan-card-button-cancel"
              onClick={handleCancelSubscription}
              variant="text"
              color="neutral"
            >
              Cancel
            </VpButton>
          )}
        </CardActions>
      </Card>
    </StyledEngineProvider>
  );
};

VpPlanCard.propTypes = {
  plan: PropTypes.oneOf(['Free', 'Pro', 'Premium', 'Enterprise', 'Appsumo']).isRequired,
  planDescription: PropTypes.string.isRequired,
  pricePerMonth: PropTypes.shape({
    monthlyBilling: PropTypes.number.isRequired,
    annualBilling: PropTypes.number.isRequired
  }).isRequired,
  isMonthlyPricing: PropTypes.bool.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  callToActionBehavior: PropTypes.oneOf([
    'SAME_PLAN',
    'ELIGIBLE_PLAN',
    'ENTERPRISE_PLAN',
    'INELIGIBLE_PLAN',
    'APPSUMO_PLAN'
  ]).isRequired,
  callToActionHandler: PropTypes.func,
  handleCancelSubscription: PropTypes.func,
  codeCount: PropTypes.number,
  planKey: PropTypes.string,
  disableUpgrade: PropTypes.bool
};

VpPlanCard.defaultProps = {
  callToActionHandler: () => {},
  handleCancelSubscription: () => {},
  pricePerMonth: {
    monthlyBilling: 0,
    annualBilling: 0
  }
};

VpPlanCard.displayName = 'VpPlanCard';

export default VpPlanCard;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpPlanCards/VpPlanCard.jsx